import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, InputGroup, FormControl } from 'react-bootstrap';
import { FaUser, FaGraduationCap, FaEnvelope, FaPhone, FaCalendar,
         FaIdCard, FaHome, FaBook, FaUserTie, FaHeart, FaSearch, FaBuilding, FaInfoCircle, FaLock, FaGlobe, FaVenusMars, FaAddressCard, FaBriefcase, FaMoneyBillWave, FaUsers,FaUniversity } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import axios from '../api/axios';
import GenericCrudPage from '../components/GenericCrudPage';
import GenericViewModal from '../components/GenericViewModal';
import {
  useColleges,
  useDegrees,
  usePrograms,
  useAcademicYears,
  useProgramYears,
  useSemesters,
  useCategories,
  useSections,
} from '../hooks/useDropdownData';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Utility functions for date conversion
const convertToDDMMYYYY = (dateString) => {
  if (!dateString || !dateString.includes('-')) return dateString; 
   const [yyyy, mm, dd] = dateString.split('-');
  return `${dd}/${mm}/${yyyy}`; 
};

const convertToYYYYMMDD = (ddmmyyyy) => {
  if (!ddmmyyyy || ddmmyyyy.includes('-')) return ddmmyyyy; 
  const [dd, mm, yyyy] = ddmmyyyy.split("/");
  return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`; 
};

const StudentProfile = () => {
  const [filterDegree, setFilterDegree] = useState("");
  const [searchStudentId, setSearchStudentId] = useState("");
  const [fetchConfig, setFetchConfig] = useState({ degree: 0 }); 
  const [showStudentViewModal, setShowStudentViewModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const { colleges, loading: collegesLoading } = useColleges();
  const { degrees, loading: degreesLoading } = useDegrees();
  const { programmes: programs, loading: programsLoading } = usePrograms();
  const { academicYears, loading: academicYearsLoading } = useAcademicYears();
  const { programmeYears: programYears, loading: programYearsLoading } = useProgramYears();
  const { semesters, loading: semestersLoading } = useSemesters();
  const { categories, loading: categoriesLoading } = useCategories();
  const { sections, loading: sectionsLoading } = useSections();

  const dropdownsLoading = collegesLoading || degreesLoading || programsLoading ||
                           academicYearsLoading || programYearsLoading || semestersLoading ||
                           categoriesLoading || sectionsLoading;

  const customFetch = async () => {
    const token = Cookies.get('token');
    const getAllUrl = `${BASE_URL}/academic/getAllStudentProfile`;
    const getByIdUrl = `${BASE_URL}/academic/getStudentProfileById`;

    const fetchFullProfiles = async (studentList) => {
      if (!studentList || studentList.length === 0) {
        return [];
      }
      try {
        const fullProfiles = await Promise.all(
          studentList.map(student =>
            axios.get(`${getByIdUrl}?id=${student.id}`, {
              headers: { Authorization: `Bearer ${token}` }
            }).then(res => {
              const profileData = res.data?.studentprofile;
              return Array.isArray(profileData) ? profileData[0] : profileData;
            }).catch(err => {
              console.error(`Failed to fetch full profile for student ID ${student.id}`, err);
              return student; 
            })
          )
        );
        return fullProfiles.filter(Boolean); // Filter out any null/undefined results
      } catch (error) {
        console.error("Error during bulk profile fetch:", error);
        return studentList; // Return original list on catastrophic failure
      }
    };

    // Case 1: Search by specific student ID
    if (searchStudentId) {
      try {
        const res = await axios.get(`${getByIdUrl}?id=${searchStudentId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const studentData = res.data?.studentprofile;
        return studentData ? (Array.isArray(studentData) ? studentData : [studentData]) : [];
      } catch (error) {
        console.error("Failed to fetch student by ID:", error);
        toast.error("Could not fetch student data for the provided ID.");
        return [];
      }
    }
    // Case 2: Filter by degree
    else if (fetchConfig.degree && fetchConfig.degree !== 0) {
      const params = new URLSearchParams({ degree: fetchConfig.degree });
      try {
        const res = await axios.get(`${getAllUrl}?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const initialList = res.data?.studentprofile || [];
        return await fetchFullProfiles(initialList); // Fetch full details
      } catch (error) {
        console.error("Failed to fetch students by degree:", error);
        toast.error("Could not fetch student data for the selected degree.");
        return [];
      }
    }
    // Case 3: Fetch all students for all degrees (initial page load)
    else {
      try {
        const res = await axios.get(getAllUrl, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const initialList = res.data?.studentprofile || [];
        return await fetchFullProfiles(initialList); // Fetch full details
      } catch (error) {
        console.error("Failed to fetch all students:", error);
        toast.error("Could not fetch all student data.");
        return [];
      }
    }
  };

  // New function to fetch a single student's full details by their ID
  const fetchStudentById = async (item) => {
    if (!item || !item.id) {
      toast.error("Invalid student selected.");
      return null;
    }
    try {
      const token = Cookies.get('token');
      const url = `${BASE_URL}/academic/getStudentProfileById`;
      const params = new URLSearchParams({ id: item.id });
      
      const res = await axios.get(`${url}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const studentData = res.data?.studentprofile;

      if (studentData && (Array.isArray(studentData) ? studentData.length > 0 : studentData)) {
        return Array.isArray(studentData) ? studentData[0] : studentData;
      } else {
        toast.warn(`Could not find full details for student ID: ${item.id}.`);
        return null;
      }
    } catch (error) {
      console.error(`Failed to fetch student by ID ${item.id}:`, error);
      toast.error("An error occurred while fetching student details.");
      return null;
    }
  };

  // Test student login function
  const testStudentLogin = async (loginName, password) => {
    try {
      const loginPayload = {
        username: loginName,
        password: password,
        loginType: "student"
      };

      const response = await axios.post(`${BASE_URL}/rest/login/doLogin`, loginPayload);
      
      if (response.data.status === "1") {
        console.log("✅ Student login test successful");
        return { success: true, data: response.data };
      } else {
        console.log("❌ Student login test failed:", response.data.message);
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error("❌ Login test error:", error);
      return { success: false, message: error.message };
    }
  };

  const fixStudentData = async (studentId) => {
    try {
      const token = Cookies.get('token');
      
      const response = await axios.get(`${BASE_URL}/academic/getStudentProfileById?id=${studentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const studentData = response.data?.studentprofile;
      if (studentData) {
        console.log("🔧 Student data found, attempting to fix login issue...");
        toast.warning(`Student created but login may not work. Please check student ID: ${studentId}`);
        return true;
      }
    } catch (error) {
      console.error("Failed to fix student data:", error);
      return false;
    }
  };

  const handleStudentCreationSuccess = async (response, form) => {
    toast.success("Student created successfully!");
    
    if (form.loginName && form.password) {
      console.log("🧪 Testing student login...");
      
      const loginTest = await testStudentLogin(form.loginName, btoa(form.password));
      
      if (!loginTest.success) {
        toast.warning("Student created but login verification failed. This may cause login issues.");
        
        const studentId = response.data?.id || response.data?.student?.id;
        if (studentId) {
          await fixStudentData(studentId);
        }
        
        console.log("Login test details:", {
          loginName: form.loginName,
          password: "****",
          error: loginTest.message
        });
      } else {
        toast.success("Student created and login verified successfully! ✅");
      }
    }
  };

  const formFields = [
    {
      title: 'Academic & Login Information',
      fields: [
        { name: 'colleges', label: 'College', icon: FaBuilding, required: true, as: 'select', options: (Array.isArray(colleges) ? colleges : []).filter(Boolean).map(c => ({ value: c.id, label: c.exName })) },
        { name: 'degree', label: 'Degree', icon: FaGraduationCap, required: true, as: 'select', options: (Array.isArray(degrees) ? degrees : []).filter(Boolean).map(d => ({ value: d.id, label: d.exName })) },
        { name: 'programmes', label: 'Program', icon: FaBook, as: 'select', options: (Array.isArray(programs) ? programs : []).filter(Boolean).map(p => ({ value: p.id, label: p.exName })) },
        { name: 'academicyear', label: 'Academic Year', icon: FaCalendar, as: 'select', options: (Array.isArray(academicYears) ? academicYears : []).filter(Boolean).map(a => ({ value: a.id, label: a.exYear })) },
        { name: 'programmeyear', label: 'Program Year', icon: FaCalendar, as: 'select', options: (Array.isArray(programYears) ? programYears : []).filter(Boolean).map(p => ({ value: p.id, label: p.exName })) },
        { name: 'semester', label: 'Semester', icon: FaCalendar, as: 'select', options: (Array.isArray(semesters) ? semesters : []).filter(Boolean).map(s => ({ value: s.id, label: s.exName })) },
        { name: 'category', label: 'Category', icon: FaIdCard, as: 'select', options: (Array.isArray(categories) ? categories : []).filter(Boolean).map(c => ({ value: c.id, label: c.exName })) },
        { name: 'section', label: 'Section', icon: FaUsers, as: 'select', options: (Array.isArray(sections) ? sections : []).filter(Boolean).map(s => ({ value: s.id, label: s.exName })) },
        { name: 'hostel', label: 'Hostel', icon: FaHome },
        { name: 'loginName', label: 'Login Name', icon: FaUser, required: true },
        { name: 'admissionSource', label: 'Admission Source', icon: FaGraduationCap },
        { name: 'password', label: 'Password', icon: FaLock, type: 'password', required: true },
      ]
    },
    // ... other form field sections remain unchanged
    {
      title: 'Personal Details',
      fields: [
        { name: 'firstName', label: 'First Name', icon: FaUser, required: true },
        { name: 'middleName', label: 'Middle Name', icon: FaUser },
        { name: 'lastName', label: 'Last Name', icon: FaUser },
        { name: 'exLocalLangName', label: 'Local Language Name', icon: FaGlobe },
        { name: 'gender', label: 'Gender', icon: FaVenusMars, as: 'select', options: [{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }, { value: 'other', label: 'Other' }] },
        { name: 'dateOfBirth', label: 'Date of Birth', icon: FaCalendar, type: 'date' },
        { name: 'exLocalLangDob', label: 'Date of Birth (Local Lang)', icon: FaGlobe },
        { name: 'adhaarNumber', label: 'Aadhaar Number', icon: FaAddressCard },
        { name: 'bloodGroup', label: 'Blood Group', icon: FaHeart },
        { name: 'motherTounge', label: 'Mother Tongue', icon: FaGlobe },
        { name: 'nationality', label: 'Nationality', icon: FaGlobe },
        { name: 'religion', label: 'Religion', icon: FaUser },
        { name: 'state', label: 'State', icon: FaHome },
        { name: 'ruralUrban', label: 'Rural/Urban', icon: FaHome, as: 'select', options: [{ value: 'rural', label: 'Rural' }, { value: 'urban', label: 'Urban' }] },
        { name: 'speciallyAbled', label: 'Specially Abled', icon: FaUser, as: 'select', options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }] },
        { name: 'income', label: 'Family Income', icon: FaMoneyBillWave },
      ]
    },
    {
      title: 'Previous Academic Details',
      fields: [
        { name: 'board', label: 'Board', icon: FaBook },
        { name: 'instituteName', label: 'Institute Name', icon: FaUniversity },
        { name: 'marks', label: 'Marks', icon: FaBook },
        { name: 'medium', label: 'Medium', icon: FaBook },
        { name: 'passingyear', label: 'Passing Year', icon: FaCalendar },
        { name: 'exLocalLangpassingYear', label: 'Passing Year (Local Lang)', icon: FaGlobe },
        { name: 'passedOutYear', label: 'Passed Out Year', icon: FaCalendar },
      ]
    },
    {
      title: 'Contact Information',
      fields: [
        { name: 'email', label: 'Email', icon: FaEnvelope, required: true, type: 'email' },
        { name: 'mobile', label: 'Mobile', icon: FaPhone, required: true },
      ]
    },
    {
      title: 'Parent/Guardian Information',
      fields: [
        { name: 'fatherName', label: 'Father Name', icon: FaUserTie },
        { name: 'fatherEmail', label: 'Father Email', icon: FaEnvelope, type: 'email' },
        { name: 'fatherMobile', label: 'Father Mobile', icon: FaPhone },
        { name: 'motherName', label: 'Mother Name', icon: FaUserTie },
        { name: 'guardian', label: 'Guardian', icon: FaUserTie },
        { name: 'occupation', label: 'Father Occupation', icon: FaBriefcase },
        { name: 'exLocalLangFatherName', label: 'Father Name (Local Lang)', icon: FaGlobe },
        { name: 'exLocalLangMotherName', label: 'Mother Name (Local Lang)', icon: FaGlobe },
      ]
    },
    {
      title: 'System Settings',
      fields: [
        { name: 'exIsActive', label: 'Active Status', icon: FaUser, as: 'switch' },
      ]
    }
  ];

  // Enhanced form validation
  const validateForm = (form) => {
    const errors = {};
    if (!form.firstName?.trim()) errors.firstName = 'First name is required';
    if (!form.email?.trim()) errors.email = 'Email is required';
    if (!form.mobile?.trim()) errors.mobile = 'Mobile is required';
    if (!form.degree) errors.degree = 'Degree is required';
    if (!form.colleges) errors.colleges = 'College is required';
    if (!form.loginName?.trim()) errors.loginName = 'Login name is required';
    if (!form.password?.trim()) errors.password = 'Password is required';
    
    const numericFields = ['colleges', 'degree'];
    numericFields.forEach(field => {
      if (form[field] && isNaN(Number(form[field]))) {
        errors[field] = `${field} must be a valid number`;
      }
    });
    
    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (form.mobile && !/^\d{10}$/.test(form.mobile.replace(/[^\d]/g, ''))) {
      errors.mobile = 'Please enter a valid 10-digit mobile number';
    }
    
    return errors;
  };

  // Fixed transform payload function
  const transformPayload = (form, editId, userId) => {
    const fieldConfig = {
      numeric: ['colleges', 'degree', 'programmes', 'academicyear', 'programmeyear', 'semester', 'category', 'section', 'hostel', 'income'],
      date: ['dateOfBirth'],
      boolean: ['exIsActive'],
    };

    const allFieldNames = formFields.flatMap(section => section.fields.map(field => field.name));
    
    const fieldsToExcludeOnEdit = [
      'degree', 'programmes', 'academicyear', 'programmeyear', 'semester'
    ];

    const finalPayload = {};
    allFieldNames.forEach(key => {
      if (editId && fieldsToExcludeOnEdit.includes(key)) return;

      let value = form[key];
      if (value === undefined || value === null) return;

      if (fieldConfig.numeric.includes(key)) {
        const numValue = Number(value);
        if (value !== '' && !isNaN(numValue)) {
          finalPayload[key] = numValue;
        }
      } else if (fieldConfig.date.includes(key)) {
        if (value) finalPayload[key] = convertToDDMMYYYY(value);
      } else if (fieldConfig.boolean.includes(key)) {
        finalPayload[key] = value ? 1 : 0;
      } else if (typeof value === 'string') {
        const trimmedValue = value.trim();
        if (trimmedValue !== '' || key === 'password') {
          if (key === 'password' && trimmedValue !== '') {
            finalPayload[key] = btoa(trimmedValue); // Encode to base64
          } else {
            finalPayload[key] = trimmedValue;
          }
        }
      } else if (value !== '') {
        finalPayload[key] = value;
      }
    });

    finalPayload.user = userId;
    finalPayload.actualcategory = 1;
    finalPayload.allocateCourses = 1;
    
    if (editId) {
      finalPayload.id = editId;
    }

    return finalPayload;
  };

  const config = {
    baseUrl: BASE_URL,
    endpoints: {
      fetch: '/academic/getAllStudentProfile',
      save: '/academic/createStudentProfile',
      update: '/academic/updateStudentProfile',
      delete: '/academic/deleteStudentProfile'
    },
    validateForm: validateForm,
    initialFormState: {
      exStudentId: "", colleges: "", degree: "", programmes: "", academicyear: "",
      programmeyear: "", semester: "", category: "", section: "", hostel: "",
      actualcategory: 1, loginName: "", password: "", exLocalLangName: "",
      firstName: "", middleName: "", lastName: "", email: "", mobile: "",
      gender: "", dateOfBirth: "", adhaarNumber: "", bloodGroup: "",
      admissionSource: "", passedOutYear: "", board: "", instituteName: "",
      marks: "", medium: "", passingyear: "", fatherName: "", fatherEmail: "",
      fatherMobile: "", motherName: "", guardian: "", occupation: "",
      exLocalLangFatherName: "", exLocalLangMotherName: "", exLocalLangpassingYear: "",
      exLocalLangDob: "", motherTounge: "", nationality: "", religion: "",
      state: "", ruralUrban: "", speciallyAbled: "", income: "",
      exIsActive: 1, allocateCourses: 1, user: 1,
    },
    entityName: 'Student',
    fetchMethod: 'CUSTOM',
    deleteMethod: 'GET',
    customFetch: customFetch,
    onCreateSuccess: handleStudentCreationSuccess,
  };

  const transformToForm = (item) => {
    if (!item) return config.initialFormState;
    return {
      ...config.initialFormState,
      ...item,
      colleges: item.exCollegeId?.toString() || "",
      degree: item.exDegreeId?.toString() || "",
      programmes: item.exProgrammesId?.toString() || "",
      academicyear: item.exAcademicyearId?.toString() || "",
      programmeyear: item.programmeyearId?.toString() || "",
      semester: item.semesterId?.toString() || "",
      category: item.exCategoryId?.toString() || "",
      section: item.sectionId?.toString() || "",
      dateOfBirth: convertToYYYYMMDD(item.dateOfBirth),
      exIsActive: item.exIsActive === 1 || item.exIsActive === true,
    };
  };

  const tableColumns = [
    { key: 'exStudentId', label: 'Student ID', sortable: true },
    { key: 'firstName', label: 'Name', sortable: true, render: (item) => `${item.firstName || ''} ${item.middleName || ''} ${item.lastName || ''}`.trim() },
    { key: 'exCollege', label: 'College', sortable: true, render: (item) => item.exCollege || '-' },
    { key: 'exProgrammes', label: 'Program', sortable: true, render: (item) => item.exProgrammes || '-' },
    { key: 'mobile', label: 'Mobile', sortable: true },
  ];

  const csvHeaders = [
    { key: 'exStudentId', label: 'Student ID' }, { key: 'firstName', label: 'First Name' },
    { key: 'middleName', label: 'Middle Name' }, { key: 'lastName', label: 'Last Name' },
    { key: 'email', label: 'Email' }, { key: 'mobile', label: 'Mobile' },
    { key: 'exCollege', label: 'College' }, { key: 'exDegree', label: 'Degree' },
    { key: 'exProgrammes', label: 'Program' }, { key: 'exAcademicyear', label: 'Academic Year' },
    { key: 'semester', label: 'Semester' },
    { key: 'exIsActive', label: 'Status', render: (item) => item.exIsActive ? 'Active' : 'Inactive' }
  ];

  const getViewFields = (item) => {
    if (!item) return [];
    return [
      { label: 'Full Name', value: `${item.firstName || ''} ${item.middleName || ''} ${item.lastName || ''}`.trim(), icon: FaUser },
      { label: 'Student ID', value: item.exStudentId, icon: FaIdCard },
      { label: 'Gender', value: item.gender, icon: FaVenusMars },
      { label: 'Date of Birth', value: item.dateOfBirth, icon: FaCalendar },
      { label: 'Blood Group', value: item.bloodGroup, icon: FaHeart },
      { label: 'Aadhaar Number', value: item.adhaarNumber, icon: FaAddressCard },
      { label: 'Nationality', value: item.nationality, icon: FaGlobe },
      { label: 'Religion', value: item.religion, icon: FaUser },
      { label: 'Mother Tongue', value: item.motherTounge, icon: FaGlobe },
      { label: 'Specially Abled', value: item.speciallyAbled, icon: FaUser },
      { label: 'Rural/Urban', value: item.ruralUrban, icon: FaHome },
      { label: 'State', value: item.state, icon: FaHome },
      { label: 'College', value: item.exCollege, icon: FaBuilding },
      { label: 'Degree', value: item.exDegree, icon: FaGraduationCap },
      { label: 'Program', value: item.exProgrammes, icon: FaBook },
      { label: 'Academic Year', value: item.exAcademicyear, icon: FaCalendar },
      { label: 'Program Year', value: item.programmeyear, icon: FaCalendar },
      { label: 'Semester', value: item.semester, icon: FaCalendar },
      { label: 'Section', value: item.section, icon: FaUsers },
      { label: 'Category', value: item.exCategory, icon: FaIdCard },
      { label: 'Admission Source', value: item.exAdmissionSource, icon: FaGraduationCap },
      { label: 'Hostel', value: item.hostel, icon: FaHome },
      { label: 'Email', value: item.email, icon: FaEnvelope },
      { label: 'Mobile', value: item.mobile, icon: FaPhone },
      { label: "Father's Name", value: item.fatherName, icon: FaUserTie },
      { label: "Father's Mobile", value: item.fatherMobile, icon: FaPhone },
      { label: "Father's Email", value: item.fatherEmail, icon: FaEnvelope },
      { label: "Mother's Name", value: item.motherName, icon: FaUserTie },
      { label: "Guardian", value: item.guardian, icon: FaUserTie },
      { label: "Father's Occupation", value: item.fatherOccupation, icon: FaBriefcase },
      { label: 'Login Name', value: item.exLoginName, icon: FaUser },
    ];
  };

  const handleSearchById = async () => {
    if (!searchStudentId) {
      toast.error("Please enter a Student ID to search.");
      return;
    }
    
    try {
        const token = Cookies.get('token');
        const url = `${BASE_URL}/academic/getStudentProfileById`;
        const params = new URLSearchParams({ id: searchStudentId });
        
        const res = await axios.get(`${url}?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const studentData = res.data?.studentprofile;

        if (studentData && (Array.isArray(studentData) ? studentData.length > 0 : studentData)) {
            const student = Array.isArray(studentData) ? studentData[0] : studentData;
            setSelectedStudent(student);
            setShowStudentViewModal(true);
        } else {
            toast.warn("Student not found with the provided ID.");
        }
    } catch (error) {
        console.error("Failed to fetch student by ID:", error);
        toast.error("An error occurred while searching for the student.");
    }
  };

  const handleClearFilters = () => {
    setFilterDegree('');
    setSearchStudentId('');
    setFetchConfig({ degree: 0 }); // Reset to fetch all
  };

  return (
    <Container fluid className="py-4">
      {dropdownsLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading dropdown data...</span>
          </div>
          <p className="mt-3 text-muted">Loading form data...</p>
        </div>
      ) : (
        <>
          <Row className="mb-4 align-items-end">
            <Col md={4}>
              <Form.Label>Filter by Degree</Form.Label>
              <Form.Select
                value={filterDegree}
                onChange={(e) => {
                  const newDegree = e.target.value;
                  setFilterDegree(newDegree);
                  setSearchStudentId(''); // Clear other filter
                  setFetchConfig({ degree: Number(newDegree) || 0 });
                }}
              >
                <option value="">All Degrees</option>
                {(Array.isArray(degrees) ? degrees : [])
                  .filter(Boolean)
                  .map((d) => (
                    <option key={d.id} value={d.id}>{d.exName}</option>
                  ))}
              </Form.Select>
            </Col>
            <Col md={5}>
              <Form.Label>Search by Student ID</Form.Label>
              <InputGroup>
                <FormControl
                  placeholder="Enter Student ID"
                  value={searchStudentId}
                  onChange={(e) => setSearchStudentId(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter') handleSearchById(); }}
                />
                <Button variant="outline-primary" onClick={handleSearchById}>
                  <FaSearch /> Search
                </Button>
              </InputGroup>
            </Col>
            <Col md={3} className="d-flex justify-content-start">
                <Button variant="outline-secondary" onClick={handleClearFilters}>Clear Filters</Button>
            </Col>
          </Row>

          <GenericCrudPage
            key={JSON.stringify(fetchConfig)}
            title="Student"
            pluralTitle="Students"
            config={config}
            formFields={formFields}
            tableColumns={tableColumns}
            emptyIcon={FaUser}
            searchQuery={searchStudentId}
            setSearchQuery={setSearchStudentId}
            csvHeaders={csvHeaders}
            csvFilename="students.csv"
            transformPayload={transformPayload}
            transformToForm={transformToForm}
            getViewFields={getViewFields}
            fetchItemForEdit={fetchStudentById}
            hideGlobalSearch={true}
          />

          <GenericViewModal
            show={showStudentViewModal}
            onHide={() => setShowStudentViewModal(false)}
            item={selectedStudent}
            title="Student"
            fields={getViewFields(selectedStudent)}
          />
        </>
      )}
    </Container>
  );
};

export default StudentProfile;